import { AS400_CDB_COLUMN, as400ColumnEditKey } from '@erp/rpc';
import type {
  As400ExportColumn,
  As400ExportGroup,
  As400ExportRow,
} from '@erp/rpc';
import {
  as400CellRules,
  isBlocking,
  rowViolations,
  type CellRuleOptions,
} from '@erp/item-master-rules';
import type { CellViolation } from '@erp/item-master-rules';
import { pendingCellRows } from './as400GridCellRules';
import type { PendingEditsByRowKey } from './as400ExportEditState';
import type { CellRowLookups } from '@erp/item-master-rules';

/**
 * The review-wide list of cells that break a rule (GEN-2747): every unsaved
 * edit on every worksheet of the review, checked against its worksheet's cell
 * rules, in the order the author meets them on screen — worksheet tab, then
 * page, then row, then column. Pure; the toolbar's error count, its
 * previous/next stepping and the error list all read it.
 */

/** One worksheet's unsaved edits: which pending scope, and its columns. */
export interface ErrorScope {
  scopeKey: string;
  group: As400ExportGroup;
  columns: readonly As400ExportColumn[];
  /** The worksheet's item-number column, which names a row in the list. */
  itemColumn: string;
}

/**
 * The row an unsaved edit was made against, and where it sat — the page and
 * the index within it — when it was edited, so the error can be found again.
 */
export interface EditedRow {
  row: As400ExportRow;
  page: number;
  index: number;
}

/** One cell that breaks a rule, located well enough to jump to it. */
export interface ReviewCellError {
  scopeKey: string;
  group: As400ExportGroup;
  rowKey: string;
  page: number;
  /** The row's item number and CDB key, as the list names the row. */
  itemNumber: string | null;
  cdb: string | null;
  violation: CellViolation;
}

/**
 * How many cells the list's entries fall on: a cell with any error counts as
 * an error, a cell with only warnings as a warning. One cell can break more
 * than one rule (a core cost in the wrong format is also not in use), and the
 * toolbar counts cells, not entries.
 */
export function countReviewCells(errors: readonly ReviewCellError[]): {
  errors: number;
  warnings: number;
} {
  const blockingByCell = new Map<string, boolean>();
  for (const error of errors) {
    const cell = [error.scopeKey, error.rowKey, error.violation.field].join(
      '\n',
    );
    const blocking = isBlocking(error.violation);
    blockingByCell.set(cell, (blockingByCell.get(cell) ?? false) || blocking);
  }
  const blockingCells = [...blockingByCell.values()].filter(Boolean).length;
  return {
    errors: blockingCells,
    warnings: blockingByCell.size - blockingCells,
  };
}

/** Where an error sits on screen, for ordering. */
interface ErrorPosition {
  scope: number;
  page: number;
  index: number;
  column: number;
}

function comparePositions(a: ErrorPosition, b: ErrorPosition): number {
  return (
    a.scope - b.scope ||
    a.page - b.page ||
    a.index - b.index ||
    a.column - b.column
  );
}

/** Each field's column position on a worksheet (its first, if repeated). */
function columnPositions(
  columns: readonly As400ExportColumn[],
): Map<string, number> {
  const positions = new Map<string, number>();
  columns.forEach((column, index) => {
    const key = as400ColumnEditKey(column);
    if (key && !positions.has(key)) positions.set(key, index);
  });
  return positions;
}

/** One scope's errors, each paired with where it sits on screen. */
function scopeErrors(
  scope: ErrorScope,
  scopeIndex: number,
  pending: PendingEditsByRowKey,
  editedRow: (rowKey: string) => EditedRow | undefined,
  options?: CellRuleOptions,
  lookups?: CellRowLookups,
): { error: ReviewCellError; position: ErrorPosition }[] {
  const rules = as400CellRules(scope.columns, options);
  if (rules.size === 0) return [];
  const columns = columnPositions(scope.columns);
  const cellRows = pendingCellRows(
    pending,
    (rowKey) => editedRow(rowKey)?.row,
    lookups,
  );
  return [...pending.values()].flatMap((edit) => {
    const located = editedRow(edit.rowKey);
    const row = cellRows.get(edit.rowKey);
    if (!row) return [];
    const page = located?.page ?? 1;
    return rowViolations(rules, row).map((violation) => ({
      error: {
        scopeKey: scope.scopeKey,
        group: scope.group,
        rowKey: edit.rowKey,
        page,
        itemNumber: row.value(scope.itemColumn),
        cdb: located?.row.values[AS400_CDB_COLUMN] ?? null,
        violation,
      },
      position: {
        scope: scopeIndex,
        page,
        index: located?.index ?? 0,
        column: columns.get(violation.field) ?? 0,
      },
    }));
  });
}

/**
 * Every rule-breaking cell among the scopes' unsaved edits, in on-screen
 * order. `editedRow` finds the row an edit was made against; a row it can't
 * place (a draft seeded by the Add-row dialog before it was ever shown) sorts
 * first on page 1, which is where drafts are pinned.
 */
export function reviewCellErrors(
  scopes: readonly ErrorScope[],
  pendingByScope: ReadonlyMap<string, PendingEditsByRowKey>,
  editedRow: (rowKey: string) => EditedRow | undefined,
  options?: CellRuleOptions,
  lookups?: CellRowLookups,
): ReviewCellError[] {
  return scopes
    .flatMap((scope, index) => {
      const pending = pendingByScope.get(scope.scopeKey);
      return pending
        ? scopeErrors(scope, index, pending, editedRow, options, lookups)
        : [];
    })
    .sort((a, b) => comparePositions(a.position, b.position))
    .map(({ error }) => error);
}

/**
 * The error to step to from `current` (null = none visited yet), wrapping
 * at either end. Stepping forward from nothing lands on the first error,
 * back from nothing on the last. Null when there are none.
 */
export function stepErrorIndex(
  current: number | null,
  count: number,
  direction: 1 | -1,
): number | null {
  if (count === 0) return null;
  if (current === null) return direction === 1 ? 0 : count - 1;
  return (((current + direction) % count) + count) % count;
}

/** A stable identity for an error: the same cell's same rule. */
export function reviewErrorKey(error: ReviewCellError): string {
  return [
    error.scopeKey,
    error.rowKey,
    error.violation.field,
    error.violation.detail.rule,
  ].join('\n');
}
